import React, {
  MouseEvent,
  ReactElement,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';
import {
  IconButton,
  MenuItem,
  Paper,
  Popover as MuiPopover,
} from '@mui/material';
import { MoreVertIcon } from 'assets/icons';
import { PopoverMenuProps } from './type';
import { Tooltip } from 'components/atoms';

export const Popover = forwardRef<
  { handleClose: () => void },
  PopoverMenuProps
>(
  (
    {
      open = true,
      menuItems,
      icon = <MoreVertIcon />,
      popoverContent,
      onButtonClick,
      contentStyles,
      iconStyles,
      menuStyles,
      anchorOrigin,
      transformOrigin,
      popoverStyles,
      disabledIconStyle,
    },
    ref,
  ) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const handleOpen = (event: MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
      event.stopPropagation();
      onButtonClick?.();
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    useImperativeHandle(ref, () => ({
      handleClose,
    }));

    return (
      <>
        <IconButton
          onClick={handleOpen}
          disableRipple={disabledIconStyle}
          sx={{
            padding: '5px',
            cursor: open ? 'pointer' : 'default',
            '&:hover': disabledIconStyle
              ? {
                  backgroundColor: 'transparent',
                }
              : {},
          }}
        >
          {React.cloneElement(icon as ReactElement, { sx: iconStyles })}
        </IconButton>
        <MuiPopover
          open={!!anchorEl && open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={
            anchorOrigin || {
              vertical: 'bottom',
              horizontal: 'right',
            }
          }
          transformOrigin={
            transformOrigin || {
              vertical: 'top',
              horizontal: 'right',
            }
          }
          sx={popoverStyles}
        >
          {popoverContent || (
            <Paper sx={{ py: 1, ...contentStyles }}>
              {menuItems
                ?.filter((item) => !item.hidden)
                ?.map((item, index) => (
                  <Tooltip
                    title={item?.tooltip}
                    key={index + item.label}
                    sx={{ zIndex: 999999 }}
                    placement="top"
                  >
                    <span>
                      <MenuItem
                        key={index + item.label}
                        sx={{ fontSize: '14px', ...menuStyles }}
                        disabled={item.disabled}
                        onClick={() => {
                          item.onClick();
                          handleClose();
                        }}
                      >
                        {item?.icon}
                        {item.label}
                      </MenuItem>
                    </span>
                  </Tooltip>
                ))}
            </Paper>
          )}
        </MuiPopover>
      </>
    );
  },
);

Popover.displayName = 'Popover';
